// Desglose de la venta por línea: producto, piezas, forma de pago y precio fijado, más el total.
import type { ReactNode } from 'react';
import { cn } from '@/lib/cn';
import { formatMXN } from '@/shared/lib/format/money';
import { SalePromotionPrice } from '@/shared/patterns/SalePromotion';
import type { LineDiscount } from '../quoteBreakdown';
import type { ChargeLine } from '../saleCharge';
import { LineName, LineSubtitle, lineIdentity, type LineSlots } from './LineLabels';

export interface ExtraChargeRow {
    label: string;
    detail: string;
    amountCents: number;
}

/** Forma de pago de la línea como la lee el vendedor; la partida lleva las dos. */
export function linePaymentLabel(line: ChargeLine): string {
    if (line.gift) return '';
    if (!line.financed) return 'Contado';
    return (line.cashCents ?? 0) > 0 ? 'Crédito SureKeep y contado' : 'Crédito SureKeep';
}

/** Reparto de la línea con montos: «$1,350.00 a crédito + $650.00 de contado». */
export function lineSplitLabel(line: ChargeLine): string {
    const parts: string[] = [];
    if (line.financedCents > 0) parts.push(`${formatMXN(line.financedCents / 100)} a crédito`);
    if ((line.cashCents ?? 0) > 0)
        parts.push(`${formatMXN((line.cashCents ?? 0) / 100)} de contado`);
    return parts.length > 0 ? parts.join(' + ') : linePaymentLabel(line);
}

export { lineIdentity };

function formatAmount(cents: number | null): string {
    return cents !== null ? formatMXN(cents / 100) : '—';
}

function Row({
    label,
    detail,
    detailTitle,
    amount,
    payment,
    testId,
}: {
    label: ReactNode;
    detail: ReactNode;
    /** Texto completo del subtítulo para el tooltip. */
    detailTitle?: string;
    amount: ReactNode;
    /** Forma de pago bajo el monto. */
    payment?: string;
    testId?: string;
}) {
    return (
        <li
            className="flex items-start gap-3 border-b border-border/60 py-2 last:border-b-0"
            data-testid={testId}
        >
            <span className="min-w-0 flex-1">
                <span className="block truncate text-[12.5px] font-semibold">{label}</span>
                <span
                    className="block truncate text-[11px] text-muted-foreground"
                    title={detailTitle}
                >
                    {detail}
                </span>
            </span>
            <span className="flex shrink-0 flex-col items-end text-right">
                <span className="text-[12.5px] font-semibold tabular-nums">{amount}</span>
                {payment && (
                    <span className="text-[11px] text-muted-foreground tabular-nums">
                        {payment}
                    </span>
                )}
            </span>
        </li>
    );
}

/**
 * `showFinanced=false` muestra solo lo que se paga en caja; `splitAmounts` pone en cada línea cuánto
 * va a crédito y cuánto de contado. `extra` antepone filas como el enganche.
 */
export function ChargeBreakdown({
    lines,
    extra = [],
    showFinanced = true,
    splitAmounts = false,
    totalLabel,
    totalCents,
    className,
    paymentLabelOf,
    slots,
    discounts,
}: {
    lines: ChargeLine[];
    extra?: ExtraChargeRow[];
    showFinanced?: boolean;
    splitAmounts?: boolean;
    totalLabel: string;
    totalCents: number;
    className?: string;
    /** Forma de pago propia de una línea (p. ej. la financiada por otra financiera). */
    paymentLabelOf?: (line: ChargeLine) => string | undefined;
    /** Piezas de Promociones para nombrar regalos y promos. */
    slots?: LineSlots;
    /** Descuento de cada línea por su `key`; tacha el precio y nombra la promo y el código. */
    discounts?: ReadonlyMap<string, LineDiscount>;
}) {
    const shown = showFinanced ? lines : lines.filter((l) => !l.financed || (l.cashCents ?? 0) > 0);
    const amountOf = (l: ChargeLine): number | null => (showFinanced ? l.totalCents : l.cashCents);
    return (
        <div className={cn('rounded-lg border border-border bg-card px-4 py-2', className)}>
            <ul>
                {extra.map((row) => (
                    <Row
                        key={row.label}
                        label={row.label}
                        detail={row.detail}
                        detailTitle={row.detail}
                        amount={formatMXN(row.amountCents / 100)}
                    />
                ))}
                {shown.map((line) => {
                    const discount = discounts?.get(line.key);
                    const amountCents = amountOf(line);
                    // En caja solo se tacha la línea de contado puro: su monto es el neto completo.
                    const originalCents =
                        showFinanced || !line.financed ? (discount?.originalCents ?? null) : null;
                    return (
                        <Row
                            key={line.key}
                            testId="charge-line"
                            label={<LineName line={line} slots={slots} />}
                            detail={
                                <LineSubtitle
                                    line={line}
                                    slots={slots}
                                    discounted={(discount?.originalCents ?? null) !== null}
                                    {...(discount?.discountCode !== undefined && {
                                        discountCode: discount.discountCode,
                                    })}
                                />
                            }
                            detailTitle={lineIdentity(line)}
                            payment={
                                paymentLabelOf?.(line) ??
                                (splitAmounts ? lineSplitLabel(line) : linePaymentLabel(line))
                            }
                            amount={
                                originalCents !== null && amountCents !== null ? (
                                    <SalePromotionPrice
                                        originalCents={originalCents}
                                        netCents={amountCents}
                                    />
                                ) : (
                                    formatAmount(amountCents)
                                )
                            }
                        />
                    );
                })}
            </ul>
            <div className="flex items-center justify-between border-t border-border pt-2 pb-1">
                <span className="text-[12.5px] font-semibold">{totalLabel}</span>
                <span className="text-[14px] font-bold tabular-nums" data-testid="charge-total">
                    {formatMXN(totalCents / 100)}
                </span>
            </div>
        </div>
    );
}
